import { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import {
  Target,
  Eye,
  ShieldCheck,
  Heart,
  Users,
  Gem,
  Search,
  Clock,
  Handshake,
  X,
  type LucideIcon,
} from 'lucide-react';

const pillars = [
  {
    icon: Target,
    title: 'Our Mission',
    description:
      'Our mission is to provide reliable, high-quality cleaning services that create clean, healthy, and comfortable environments for our clients. We are committed to professionalism, attention to detail, customer satisfaction, and environmentally responsible cleaning practices.',
  },
  {
    icon: Eye,
    title: 'Our Vision',
    description:
      'Our vision is to redefine the meaning of clean by creating spaces where people feel healthier, happier, and more productive. We aspire to be recognized not only for the quality of our work, but for the trust, care, and peace of mind we bring to every client we serve.',
  },
];

const CORE_VALUES: { icon: LucideIcon; title: string; description: string }[] = [
  {
    icon: ShieldCheck,
    title: 'Confidentiality',
    description: "We respect your privacy and keep your home or office details confidential at all times.",
  },
  {
    icon: Heart,
    title: 'Integrity',
    description: "We do what's right, even when no one is watching — honesty guides every clean.",
  },
  {
    icon: Users,
    title: 'Teamwork',
    description: 'Our cleaners collaborate seamlessly to deliver consistent, reliable results every visit.',
  },
  {
    icon: Gem,
    title: 'Quality',
    description: 'We hold ourselves to the highest standard, leaving every space spotless and refreshed.',
  },
  {
    icon: Search,
    title: 'Attention to Detail',
    description: 'No corner is overlooked — we notice the small things that make a big difference.',
  },
  {
    icon: Clock,
    title: 'Reliability',
    description: 'You can count on us to show up on time and deliver the same great service every time.',
  },
  {
    icon: Handshake,
    title: 'Respect',
    description: 'We treat your home, office, and belongings with the same care and consideration as our own.',
  },
];

export default function MissionVisionValues() {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-100px' });
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <section id="mission" className="relative py-24 sm:py-32 bg-white overflow-hidden scroll-mt-28">
      <div className="max-w-7xl mx-auto section-padding relative z-10">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-semibold text-axis-500 uppercase tracking-[0.25em]">
            AXIS Cleaning Services LLC
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-axis-950 leading-tight mt-3">
            Mission, Vision <span className="gradient-text italic">&amp;</span> Values
          </h2>
        </motion.div>

        {/* Mission / Vision Cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-20">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white rounded-3xl p-8 lg:p-10 border border-axis-100/70 shadow-sm hover:shadow-lg hover:shadow-axis-500/5 transition-shadow duration-300"
            >
              <div className="w-14 h-14 rounded-2xl clean-gradient flex items-center justify-center mb-6 shadow-md shadow-axis-500/20">
                <pillar.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-2xl font-bold text-axis-950 mb-3">{pillar.title}</h3>
              <p className="text-gray-600 leading-relaxed">{pillar.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Core Values Heading */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-display text-3xl sm:text-4xl font-bold text-axis-950 text-center mb-10"
        >
          Our <span className="text-axis-500 italic">Core Values</span>
        </motion.h3>

        {/* Core Values Grid */}
        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {CORE_VALUES.map((value, i) => {
            const isActive = selected === i;
            return (
              <motion.button
                key={value.title}
                type="button"
                onClick={() => setSelected(isActive ? null : i)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`w-40 rounded-2xl p-6 text-center border transition-all duration-300 ${
                  isActive
                    ? 'bg-axis-50 border-axis-300 shadow-lg shadow-axis-500/10 -translate-y-1'
                    : 'bg-sand-50 border-sand-100 hover:bg-axis-50/60 hover:border-axis-200 hover:-translate-y-1'
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
                  <value.icon className="w-5 h-5 text-axis-600" />
                </div>
                <span className="text-sm font-semibold text-axis-900">{value.title}</span>
              </motion.button>
            );
          })}
        </div>

      </div>

      {/* Description Modal */}
      <AnimatePresence>
        {selected !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-axis-950/50 backdrop-blur-sm p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl shadow-axis-950/30"
            >
              <button
                onClick={() => setSelected(null)}
                aria-label="Close"
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-14 h-14 rounded-2xl clean-gradient flex items-center justify-center mb-6 shadow-md shadow-axis-500/20">
                {(() => {
                  const ActiveIcon = CORE_VALUES[selected].icon;
                  return <ActiveIcon className="w-6 h-6 text-white" />;
                })()}
              </div>
              <h4 className="font-display text-2xl font-bold text-axis-950 mb-3">{CORE_VALUES[selected].title}</h4>
              <p className="text-gray-600 leading-relaxed">{CORE_VALUES[selected].description}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
